import { ProductVariantFlag } from '@/components/_products/ProductVariantFlag'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselThumbs,
} from '@/components/_ui/Carousel'
import type {
  CosmicMediaType,
  CosmicProductType,
  CosmicVariationFlagType,
  CosmicVariationType,
} from '@/lib/cms/cosmic/types/CosmicProductType'
import { cn } from '@/lib/utils'
import Image from 'next/image'
import { useState } from 'react'

type Props = {
  images: CosmicMediaType[]
  flagData?: CosmicVariationFlagType
  className?: string
  selectedVariation?: CosmicVariationType
  productData?: CosmicProductType
}

const ProductCarousel = ({ images, flagData, className }: Props) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedIndex, setSelectedIndex] = useState<number>(0)

  const renderCarouselImages = () => {
    if (images.length === 0) {
      return (
        <CarouselItem data-test={`product-carousel-item-1`}>
          <Image
            priority={true}
            src="/misc/no-product.png"
            alt="Placeholder: no product"
            height={600}
            width={650}
            className=" w-full overflow-hidden rounded-lg object-contain"
          />
        </CarouselItem>
      )
    }

    return images.map((image, index) => (
      <CarouselItem
        key={index}
        data-test={`product-carousel-item-${image.altText}`}
      >
        <Image
          {...(index === 0 && { priority: true })} // Load first image with priority
          src={image.url}
          blurDataURL={
            'data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnID48ZmlsdGVyIGlkPSdiJyBjb2xvci1pbnRlcnBvbGF0aW9uLWZpbHRlcnM9J3NSR0InPjxmZUdhdXNzaWFuQmx1ciBzdGREZXZpYXRpb249JzIwJy8+PGZlQ29sb3JNYXRyaXggdmFsdWVzPScxIDAgMCAwIDAgMCAxIDAgMCAwIDAgMCAxIDAgMCAwIDAgMCAxMDAgLTEnIHJlc3VsdD0ncycvPjxmZUZsb29kIHg9JzAnIHk9JzAnIHdpZHRoPScxMDAlJyBoZWlnaHQ9JzEwMCUnLz48ZmVDb21wb3NpdGUgb3BlcmF0b3I9J291dCcgaW49J3MnLz48ZmVDb21wb3NpdGUgaW4yPSdTb3VyY2VHcmFwaGljJy8+PGZlR2F1c3NpYW5CbHVyIHN0ZERldmlhdGlvbj0nMjAnLz48L2ZpbHRlcj48aW1hZ2Ugd2lkdGg9JzEwMCUnIGhlaWdodD0nMTAwJScgeD0nMCcgeT0nMCcgcHJlc2VydmVBc3BlY3RSYXRpbz0nbm9uZScgc3R5bGU9J2ZpbHRlcjogdXJsKCNiKTsnIGhyZWY9J2RhdGE6aW1hZ2Uvd2VicDtiYXNlNjQsVWtsR1JsZ0FBQUJYUlVKUVZsQTRXQW9BQUFBUUFBQUFDUUFBQ1FBQVFVeFFTQk1BQUFBQkQvRDgvNGlJSUJBZzhQOXRJYUwvYWF3QkFGWlFPQ0FlQUFBQU1BRUFuUUVxQ2dBS0FBekF6aVVBQUc0QUFQN3FyNnpUQk1qdGVIQUEnLz48L3N2Zz4='
          }
          placeholder="blur"
          alt={image.altText}
          height={600}
          width={650}
          className=" w-full object-contain"
        />
      </CarouselItem>
    ))
  }

  return (
    <div
      className={cn(
        'flex w-full flex-col items-center justify-center md:sticky md:top-10 md:h-full md:w-1/2',
        className,
      )}
      data-test="product-carousel"
    >
      <Carousel
        opts={{
          align: 'start',
        }}
      >
        {flagData?.label && (
          <div className="hidden md:block">
            <ProductVariantFlag
              text={flagData?.label ?? ''}
              textColor={flagData?.style?.textColor ?? ''}
              backgroundColor={flagData?.style?.backgroundColor ?? ''}
              className="absolute -top-2 right-3"
            />
          </div>
        )}

        <CarouselContent>{renderCarouselImages()}</CarouselContent>
        <div
          className="mt-5 flex justify-center md:mt-14"
          data-test="product-carousel-thumbs"
        >
          <CarouselThumbs
            className="hidden lg:block"
            images={images}
            indexCallBack={setSelectedIndex}
            blurDataUrl={
              'data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnID48ZmlsdGVyIGlkPSdiJyBjb2xvci1pbnRlcnBvbGF0aW9uLWZpbHRlcnM9J3NSR0InPjxmZUdhdXNzaWFuQmx1ciBzdGREZXZpYXRpb249JzIwJy8+PGZlQ29sb3JNYXRyaXggdmFsdWVzPScxIDAgMCAwIDAgMCAxIDAgMCAwIDAgMCAxIDAgMCAwIDAgMCAxMDAgLTEnIHJlc3VsdD0ncycvPjxmZUZsb29kIHg9JzAnIHk9JzAnIHdpZHRoPScxMDAlJyBoZWlnaHQ9JzEwMCUnLz48ZmVDb21wb3NpdGUgb3BlcmF0b3I9J291dCcgaW49J3MnLz48ZmVDb21wb3NpdGUgaW4yPSdTb3VyY2VHcmFwaGljJy8+PGZlR2F1c3NpYW5CbHVyIHN0ZERldmlhdGlvbj0nMjAnLz48L2ZpbHRlcj48aW1hZ2Ugd2lkdGg9JzEwMCUnIGhlaWdodD0nMTAwJScgeD0nMCcgeT0nMCcgcHJlc2VydmVBc3BlY3RSYXRpbz0nbm9uZScgc3R5bGU9J2ZpbHRlcjogdXJsKCNiKTsnIGhyZWY9J2RhdGE6aW1hZ2Uvd2VicDtiYXNlNjQsVWtsR1JsZ0FBQUJYUlVKUVZsQTRXQW9BQUFBUUFBQUFDUUFBQ1FBQVFVeFFTQk1BQUFBQkQvRDgvNGlJSUJBZzhQOXRJYUwvYWF3QkFGWlFPQ0FlQUFBQU1BRUFuUUVxQ2dBS0FBekF6aVVBQUc0QUFQN3FyNnpUQk1qdGVIQUEnLz48L3N2Zz4='
            }
          />
        </div>
      </Carousel>
    </div>
  )
}

export default ProductCarousel
